import SamaritanFetch from './httpClient';
import { Organization, Deployments } from 'types';
import { shouldHideIntakeDeployment } from 'util/user';

export interface OrganizationPhaseCommitments {
    organization: Organization;
    commitments: {
        id: number;
        deployment_id: number;
        deployment_name: string;
        phase_id: number;
        seat_total: number;
        seats_used_actual_in_phase: number;
    }[]
    user_signed_up: number;
}

const fetchUserSignups = async (commitmentIds: number[], interviewerId: number): Promise<number> => {
    const queryParams = [
        `samaritan_members[interviewer_id][]=${interviewerId}`,
        `template=micro`,
        ...commitmentIds.map(id => `samaritan_members[organization_phase_commitment_id][]=${id}`)
    ].join('&');
    const response = await SamaritanFetch.v3(`/samaritan_members?${queryParams}`);
    if (!response.ok) {
        throw new Error('Failed to fetch user signups');
    }
    const data = await response.json();
    return data.samaritan_members.length;
};

export const fetchActiveOrganizationCommitments = async (organizationId: number, interviewerId: number): Promise<OrganizationPhaseCommitments> => {
    const response = await SamaritanFetch.v3(`/organizations/${organizationId}?organization[relations][organization_phase_commitments][phase]{}`);
    if (!response.ok) {
        throw new Error('Failed to fetch organization commitments');
    }
   
    const data = await response.json();
    const organization: Organization = data.organization;

    // Get active phases from deployments
    const activePhases = organization.deployments
        .map(deployment => {
            const phase = deployment.most_recent_phase;
            if (!phase) return null;
            
            const endDate = new Date(phase.end_date);
            const startDate = new Date(phase.start_date);
            const now = new Date();
            
            return (now >= startDate && now <= endDate) ? phase.id : null;
        })
        .filter((id): id is number => id !== null);

    // Filter commitments to only include active phases
    const commitments = organization.relations?.organization_phase_commitments || [];
    const filteredCommitments = commitments
        .filter(commitment => activePhases.includes(commitment.phase_id))
        .map(commitment => ({
            id: commitment.id,
            deployment_id: commitment.deployment_id,
            deployment_name: organization.deployments.find(deployment => deployment.id === commitment.deployment_id)?.name || '',
            phase_id: commitment.phase_id,
            seat_total: commitment.seat_total,
            seats_used_actual_in_phase: commitment.seats_used_actual_in_phase
        }));

    const user_signed_up = await fetchUserSignups(filteredCommitments.map(c => c.id), interviewerId);

    return {
        organization,
        commitments: filteredCommitments,
        user_signed_up
    };
};

export const getIntakeDeploymentOptions = (deployments: Deployments[] | undefined): Deployments[] => {
    return (deployments || []).filter(
        (deployment) => !shouldHideIntakeDeployment(deployment)
    );
};
